import html2Element from "@/utils/html2Element.js";
import showMessage from "@/utils/showMessage.js";
import processAjaxResponse from "@/utils/processAjaxResponse";

/*
<div class="files" 
    data-widget="upload" 
    data-max-files="10" 
    data-max-size="5242880" 
    data-types="image/jpeg,image/png,image/webp"
    data-button-text="Загрузить файл" 
    data-close-button="×" 
    data-msg-too-many-files="Слишком много файлов",
    data-msg-invalid-type="Неправильный тип файла &quot;[FILENAME]&quot;",
    data-msg-too-large="Слишком большой файл &quot;[FILENAME]&quot;",
    data-msg-upload-failed="Не удалось загрузить файл &quot;[FILENAME]&quot;",
    ></div>
*/

// Виджет для загрузки файлов
export default function fileUploadWidget(root, data) {
    const {
        name,
        buttonText,
        maxFiles = Number.POSITIVE_INFINITY,
        maxSize = Number.POSITIVE_INFINITY,
        chunkSize = 1048576,
        msgTooManyFiles = "Слишком много файлов",
        msgInvalidType = "Неправильный тип файла \"[FILENAME]\"",
        msgTooLarge = "Слишком большой файл \"[FILENAME]\"",
        msgUploadFailed = "Не удалось загрузить файл \"[FILENAME]\"",
        types = "image/jpeg,image/png,image/webp",
        closeButton = "×"
    } = data;

    let filesList = [];

    const formElement = root.closest("form");
    const listElement = html2Element(`<div class="files-list"></div>`);
    const uploadButtonWrapperElement = createUploadButtonWrapper(buttonText, types, maxFiles);
    const uploadButtonElement = uploadButtonWrapperElement.querySelector('.files-input');

    uploadButtonElement.addEventListener("change", handleFileSelection);

    root.append(listElement);
    root.append(uploadButtonWrapperElement);

    updateFileList();

    formElement.addEventListener("clear", function () {
        filesList = [];
        updateFileList();
    });

    function createUploadButtonWrapper(buttonText, types, maxFiles) {
        const wrapper = html2Element(`
            <div class="files-upload-button" style="display: inline-flex; align-items: center; justify-content: center; position: relative; overflow: hidden;">
                ${buttonText}
                <input class="files-input" type="file" accept="${types}" style="position: absolute; left: 0; top: 0; font-size: 100px; opacity: 0;" />
            </div>
        `);

        if (maxFiles > 1) {
            wrapper.querySelector('.files-input').setAttribute("multiple", true);
        }

        return wrapper;
    }

    function handleFileSelection() {
        let files = Array.from(uploadButtonElement.files);

        if (filesList.length + files.length > maxFiles) {
            showMessage(msgTooManyFiles);
            files.length = maxFiles - filesList.length;
        }

        files = filterValidFiles(files);

        if (files.length < 1) {
            return;
        }

        root.classList.add("loading");

        uploadFilesLoop(files, () => {
            root.classList.remove("loading");
            updateFileList();
        });
    }

    const allowedTypes = types
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);

    function isAllowedType(file) {
        if (allowedTypes.length < 1) {
            return true;
        }

        const fileType = (file.type || "").toLowerCase();
        const fileName = (file.name || "").toLowerCase();

        return allowedTypes.some((token) => {
            if (token.startsWith(".")) {
                return fileName.endsWith(token);
            }
            return fileType === token;
        });
    }

    function filterValidFiles(files) {
        return files.filter((file) => {
            if (!isAllowedType(file)) {
                showMessage(msgInvalidType.replace("[FILENAME]", file.name));
                return false;
            }

            if (file.size > maxSize) {
                showMessage(msgTooLarge.replace("[FILENAME]", file.name));
                return false;
            }

            return true;
        });
    }

    function dec2hex(dec) {
        return dec < 10 ? "0" + String(dec) : dec.toString(16);
    }

    function generateId(len) {
        var arr = new Uint8Array((len || 40) / 2);
        window.crypto.getRandomValues(arr);
        return Array.from(arr, dec2hex).join("");
    }

    function uploadFilesLoop(files, onDone) {
        if (files.length > 0) {
            var file = files.shift();
            var id = generateId(20);

            uploadFileLoop(id, file, 0, function () {
                filesList.push({
                    id: id,
                    name: file.name,
                });

                setTimeout(function () {
                    uploadFilesLoop(files, onDone);
                }, 1);

                updateFileList();
            });
        } else {
            onDone();
            return;
        }
    }

    async function uploadFileLoop(id, file, offset, onDone) {
        var formData = new FormData();

        if (offset > file.size) {
            onDone();
            return;
        }

        var blob = file.slice(offset, offset + chunkSize);

        formData.append("id", id);
        formData.append("data", blob);

        try {
            const response = await fetch("/_ajax/files/upload", {
                method: 'POST',
                body: formData,
                mode: "same-origin",
                cache: "no-cache",
                credentials: "include",
                redirect: "follow",
                referrer: "no-referrer",
                //timeout: 30000,
            });

            const responseText = await response.text();
            const responseJson = JSON.parse(responseText);

            processAjaxResponse(responseJson, (message, data) => {
                setTimeout(function () {
                    uploadFileLoop(id, file, offset + chunkSize, onDone);
                }, 1);
            }, (message, data) => { // В случае ошибки
                showMessage(message);
            });
        } catch (e) {
            console.log(e);

            root.classList.remove("loading");
            uploadButtonElement.value = null;

            setTimeout(function () {
                uploadFileLoop(id, file, offset + chunkSize, onDone);
            }, 1);

            showMessage(msgUploadFailed.replace("[FILENAME]", file.name));
        }
    }

    function updateFileList() {
        listElement.innerHTML = "";
        for (const [index, entry] of filesList.entries()) {
            const listEntry = html2Element(`<div class="files-list-entry">
                <div class="files-list-entry-text">${entry.name}</div>
                <button type="button" class="files-list-entry-remove">${closeButton}</button>
                <input class="files-list-entry-tmp" type="hidden" name="${name}[${index}].tmp"/>
                <input class="files-list-entry-name" type="hidden" name="${name}[${index}].name"/>
            </div>`);

            listEntry.querySelector(".files-list-entry-tmp").value = entry.id;
            listEntry.querySelector(".files-list-entry-name").value = entry.name;
            listEntry.querySelector(".files-list-entry-remove").addEventListener("click", function () {
                filesList = filesList.filter((e) => e.id != entry.id);
                updateFileList();
            });

            listElement.append(listEntry);
        }

        if (filesList.length >= maxFiles) {
            uploadButtonWrapperElement.style.display = "none";
        } else {
            uploadButtonWrapperElement.style.display = "inline-flex";
        }
    }
}
